import { useCallback, useEffect, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import Barcode from '@/components/feature/Barcode';
import type { Client } from '@/lib/types';

interface FormState {
  name: string;
  rnc: string;
  phone: string;
  address: string;
  email: string;
  company: string;
  contact_name: string;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  rnc: '',
  phone: '',
  address: '',
  email: '',
  company: '',
  contact_name: '',
  active: true,
};

export default function WholesaleClients() {
  const [items, setItems] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [cardClient, setCardClient] = useState<Client | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('clients')
        .select('*')
        .eq('is_wholesale', true)
        .order('created_at', { ascending: false });
      if (e) throw e;
      setItems((data as Client[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los clientes mayoristas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (c: Client) => {
    setEditing(c);
    setForm({
      name: c.name,
      rnc: c.rnc ?? '',
      phone: c.phone ?? '',
      address: c.address ?? '',
      email: c.email ?? '',
      company: c.company ?? '',
      contact_name: c.contact_name ?? '',
      active: c.active,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        rnc: form.rnc.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        email: form.email.trim() || null,
        company: form.company.trim() || null,
        contact_name: form.contact_name.trim() || null,
        active: form.active,
      };
      if (editing) {
        const { error: e } = await supabase
          .from('clients')
          .update(payload)
          .eq('id', editing.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase
          .from('clients')
          .insert({ ...payload, is_wholesale: true })
          .select('*')
          .single();
        if (e) throw e;
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c: Client) => {
    const { error: e } = await supabase
      .from('clients')
      .update({ active: !c.active })
      .eq('id', c.id);
    if (e) return;
    load();
  };

  const printCard = (c: Client) => {
    const img = document.createElement('img');
    JsBarcode(img, c.wholesale_code ?? '', {
      format: 'CODE128',
      height: 60,
      displayValue: true,
      fontSize: 16,
      margin: 4,
      lineColor: '#000000',
      background: '#ffffff',
    });
    const w = window.open('', '_blank', 'width=420,height=560');
    if (!w) return;
    w.document.write(
      `<html><head><title>${c.name}</title></head><body style="font-family:sans-serif;padding:24px;">` +
        `<div style="border:2px solid #000;border-radius:12px;padding:24px;text-align:center;max-width:340px;margin:auto;">` +
        `<h2 style="margin:0 0 4px">${c.company || c.name}</h2>` +
        `<p style="margin:0 0 4px;font-size:14px">${c.name}</p>` +
        `<p style="margin:0 0 4px;font-size:13px">${c.rnc ? 'RNC: ' + c.rnc : ''}</p>` +
        `<p style="margin:0 0 4px;font-size:13px">${c.phone ? 'Tel: ' + c.phone : ''}</p>` +
        `<p style="margin:0 0 16px;font-size:16px;font-weight:bold">${c.wholesale_code}</p>` +
        `<div><img src="${img.src}" style="max-width:100%"/></div>` +
        `<p style="margin:16px 0 0;font-size:13px">Cliente mayorista</p>` +
        `</div>` +
        `<script>window.onload=function(){window.print()}<\/script>` +
        `</body></html>`
    );
    w.document.close();
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-600">
          {items.length} {items.length === 1 ? 'cliente mayorista' : 'clientes mayoristas'}.
        </p>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-add-line" /> Registrar mayorista
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-user-star-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay clientes mayoristas registrados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-foreground-950 truncate">
                    {c.company || c.name}
                  </p>
                  {c.company && <p className="text-sm text-foreground-600">{c.name}</p>}
                  <p className="mt-1 text-xs font-mono font-semibold text-primary-700">
                    {c.wholesale_code}
                  </p>
                </div>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                    c.active ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'
                  }`}
                >
                  {c.active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <div className="mt-3 space-y-1 text-sm text-foreground-600">
                {c.rnc && <p>RNC: {c.rnc}</p>}
                {c.phone && (
                  <p className="flex items-center gap-1.5">
                    <i className="ri-phone-line text-foreground-400" /> {c.phone}
                  </p>
                )}
                {c.email && <p>{c.email}</p>}
                {c.address && <p className="truncate">{c.address}</p>}
              </div>

              <div className="mt-4 flex justify-center border-t border-background-200 pt-3">
                <Barcode value={c.wholesale_code ?? ''} height={40} />
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setCardClient(c)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                >
                  <i className="ri-id-card-line" /> Ver tarjeta
                </button>
                <button
                  onClick={() => openEdit(c)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                >
                  <i className="ri-edit-line" /> Editar
                </button>
                <button
                  onClick={() => toggle(c)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
                    c.active
                      ? 'border border-background-300 text-foreground-700 hover:bg-background-100'
                      : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  }`}
                >
                  <i className={c.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                  {c.active ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar cliente mayorista' : 'Registrar cliente mayorista'}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Nombre *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className={inputClass}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Cédula / RNC</label>
              <input
                value={form.rnc}
                onChange={(e) => setForm((p) => ({ ...p, rnc: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Teléfono</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Empresa</label>
            <input
              value={form.company}
              onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Contacto</label>
            <input
              value={form.contact_name}
              onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Dirección</label>
            <input
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              className={inputClass}
            />
          </div>
          {!editing && (
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
              El código mayorista (MAY-XXXXXX) se genera automáticamente al guardar.
            </div>
          )}
          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <span className="text-sm font-medium text-foreground-900">Cliente activo</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>
          {error && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {error}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={cardClient != null}
        onClose={() => setCardClient(null)}
        title="Tarjeta de cliente mayorista"
        footer={
          <>
            <button
              onClick={() => setCardClient(null)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cerrar
            </button>
            <button
              onClick={() => cardClient && printCard(cardClient)}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-printer-line" /> Imprimir tarjeta
            </button>
          </>
        }
      >
        {cardClient && (
          <div className="text-center">
            <p className="font-heading font-semibold text-foreground-950">
              {cardClient.company || cardClient.name}
            </p>
            <p className="text-sm text-foreground-600">{cardClient.name}</p>
            <div className="mt-2 space-y-0.5 text-sm text-foreground-600">
              {cardClient.rnc && <p>RNC: {cardClient.rnc}</p>}
              {cardClient.phone && <p>Tel: {cardClient.phone}</p>}
            </div>
            <p className="mt-3 text-lg font-mono font-semibold text-foreground-950">
              {cardClient.wholesale_code}
            </p>
            <div className="mt-4 flex justify-center">
              <Barcode value={cardClient.wholesale_code ?? ''} height={60} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}